/* eslint-disable react/prop-types */
import React from 'react';
import PropTypes from 'prop-types';
import { Checkbox } from 'antd';
import head from 'lodash/head';
import isNil from 'lodash/isNil';
import classNames from 'classnames';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import { Formik, Field, ErrorMessage, Form, useFormikContext } from 'formik';

import { DATE_FORMAT } from 'components/constants';
import ComboBox from '../ComboBox/ComboBox';
import { Input } from 'components/AntD';
import ActionButtons from 'components/ActionButtons';
import './styles.less';

function ErrorText({ name }) {
  return (
    <ErrorMessage component="div" className="tw-min-h-4" name={name}>
      {msg => {
        msg = Array.isArray(msg) ? msg.find(m => !!m) : msg;
        if (!msg) {
          return <div className="tw-h-4" />;
        }
        const error =
          React.isValidElement(msg) || typeof msg === 'string' ? msg : head(Object.values(msg));
        return (
          <label htmlFor={name} className="form-text tw-text-sm tw-text-error">
            {error}
          </label>
        );
      }}
    </ErrorMessage>
  );
}

function ComboBoxWrapper({ name, value, form, ...rest }) {
  const { setFieldValue, setFieldTouched } = form;
  return (
    <ComboBox
      id={name}
      selectedItems={value}
      addAnswerFunction={items => {
        setFieldValue(name, items);
        setFieldTouched(name, true);
      }}
      {...rest}
    />
  );
}

function MultiCheckboxWrapper(props) {
  const { onChange, checkboxLabel, ...rest } = props;

  return (
    <Checkbox
      onChange={e => {
        onChange(e, e.target.checked);
      }}
      {...rest}
    >
      {checkboxLabel}
    </Checkbox>
  );
}

function InputField({
  label,
  description,
  field,
  fieldComponent: FieldComponent = Input,
  fieldClassName = '',
  ...rest
}) {
  return (
    <div
      className={classNames(
        fieldClassName,
        `form-group${fieldClassName.includes('tw-mb') ? '' : ' tw-mb-4'}`
      )}
    >
      {label && typeof label === 'string' ? (
        <label className="tw-block tw-text-sm" htmlFor={field.name}>
          {label}
        </label>
      ) : (
        <>{label}</>
      )}
      {description}
      <FieldComponent
        size="large"
        autoComplete="off"
        className="tw-block tw-bg-background hover:tw-bg-white active:tw-bg-white tw-w-full"
        id={field.name}
        {...field}
        {...rest}
      />
      <ErrorText name={field.name} />
    </div>
  );
}

InputField.propTypes = {
  fieldComponent: PropTypes.elementType,
  description: PropTypes.node,
  label: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  field: PropTypes.object,
  form: PropTypes.object,
  disabled: PropTypes.bool,
  fieldClassName: PropTypes.string,
};

function FormContainer({
  children,
  onSecondaryButtonClick,
  primaryButtonDisabled,
  primaryButtonText,
  secondaryButtonDisabled,
  secondaryButtonText,
  secondaryActionProps,
  primaryActionProps,
  error,
  className = '',
  hideActionButtons = false,
  ...rest
}) {
  const formProps = useFormikContext();
  return (
    <Form
      data-clarity-mask="false"
      autoComplete="off"
      className={classNames(className, 'form-container')}
      onKeyDown={e => {
        if (e.key === 'Enter') {
          formProps.handleSubmit();
        }
      }}
      {...rest}
    >
      {children}
      {!hideActionButtons && (
        <ActionButtons
          className="tw-mt-3"
          size={onSecondaryButtonClick ? 'middle' : 'large'}
          isLoading={formProps.isSubmitting && !error}
          secondaryActionCallback={onSecondaryButtonClick}
          secondaryActionProps={secondaryActionProps}
          primaryActionProps={{
            ...(primaryActionProps || {}),
            disabled: isNil(primaryButtonDisabled)
              ? Object.values(formProps.values).every(val => !val)
              : primaryButtonDisabled,
          }}
          secondaryActionText={secondaryButtonText}
          primaryActionText={primaryButtonText}
          secondaryActionDisabled={secondaryButtonDisabled}
          error={error}
        />
      )}
    </Form>
  );
}

FormContainer.propTypes = {
  children: PropTypes.node,
  onSecondaryButtonClick: PropTypes.func,
  primaryButtonText: PropTypes.node,
  secondaryButtonText: PropTypes.node,
  className: PropTypes.string,
  size: PropTypes.string,
  hideActionButtons: PropTypes.bool,
};

function DatePickerField({ label, disabled, name, values, placeholder, openToDate }) {
  const formProps = useFormikContext();
  const { [name]: value } = values || {};
  const onChange = React.useCallback(
    date => {
      formProps.setFieldTouched(name, true);
      formProps.setFieldValue(name, date);
    },
    [formProps, name]
  );
  const openDate = React.useMemo(() => {
    if (openToDate) {
      const date = moment(value || openToDate, DATE_FORMAT);
      if (date.isValid()) {
        return date.toDate();
      }
      moment(value).toDate();
    }
  }, [openToDate, value]);
  return (
    <div className="form-group tw-mb-4 tw-flex tw-flex-col date-picker-field">
      {label && (
        <label className="tw-text-sm" htmlFor={name}>
          {label}
        </label>
      )}
      <DatePicker
        className="form-control"
        id={name}
        selected={(value && new Date(value)) || null}
        disabled={disabled}
        onChange={onChange}
        placeholderText={placeholder}
        openToDate={openDate}
      />
      <ErrorText name={name} />
    </div>
  );
}

export {
  InputField,
  Field,
  Formik,
  FormContainer,
  ComboBoxWrapper,
  ErrorText,
  MultiCheckboxWrapper,
  DatePickerField,
};
